import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../lib/store';
import { dashboardApi } from '../lib/api';
import {
    Map,
    BarChart3,
    FileText,
    TrendingUp,
    Plus,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface DashboardStats {
    total_polygons: number;
    total_area_ha: number;
    analysis_completed: number;
    compliance_rate: number | null;
    compliance_summary: { status: string; count: number }[];
    recent_activity: {
        id: string;
        type: string;
        name: string;
        status: string;
        compliance_status: string | null;
        date: string | null;
    }[];
}

export default function DashboardPage() {
    const { t, i18n } = useTranslation();
    const user = useAuthStore((state) => state.user);
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const data = await dashboardApi.getStats();
                setStats(data);
            } catch (err) {
                console.error('Error loading dashboard stats:', err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const complianceColorMap: Record<string, string> = {
        compliant: 'text-green-600',
        non_compliant: 'text-red-600',
        inconclusive: 'text-yellow-500',
        pending: 'text-gray-400',
    };

    const complianceLabelMap: Record<string, string> = {
        compliant: t('compliance.compliant'),
        non_compliant: t('compliance.nonCompliant'),
        inconclusive: t('compliance.inconclusive'),
        pending: t('compliance.pending'),
    };

    const dateLocale = i18n.language === 'es' ? 'es-ES' : 'en-US';

    return (
        <div className="space-y-6 sm:space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                    {t('dashboard.welcome')}, {user?.full_name?.split(' ')[0]}!
                </h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                    {t('dashboard.subtitle')}
                </p>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            ) : !stats ? (
                <div className="card p-8 text-center">
                    <p className="text-gray-500">{t('dashboard.errorLoading')}</p>
                </div>
            ) : (
                <>
                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <StatCard
                            icon={Map}
                            label={t('dashboard.totalPolygons')}
                            value={stats.total_polygons.toString()}
                            color="primary"
                        />
                        <StatCard
                            icon={BarChart3}
                            label={t('dashboard.totalArea')}
                            value={`${stats.total_area_ha.toLocaleString()} ha`}
                            color="forest"
                        />
                        <StatCard
                            icon={FileText}
                            label={t('dashboard.analysisCompleted')}
                            value={stats.analysis_completed.toString()}
                            color="blue"
                        />
                        <StatCard
                            icon={TrendingUp}
                            label={t('dashboard.complianceRate')}
                            value={
                                stats.compliance_rate !== null
                                    ? `${stats.compliance_rate}%`
                                    : '—'
                            }
                            color="green"
                            highlight={stats.compliance_rate !== null && stats.compliance_rate >= 90}
                        />
                    </div>

                    {/* Quick Actions */}
                    <div className="card p-4 sm:p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            {t('dashboard.quickActions')}
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            <Link
                                to="/polygons"
                                className="flex items-center gap-4 p-4 rounded-xl border border-line dark:border-gray-700 hover:bg-cream dark:hover:bg-gray-800 transition-colors"
                            >
                                <div className="w-11 h-11 rounded-xl bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 flex items-center justify-center">
                                    <Plus className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">
                                        {t('polygons.addPolygon')}
                                    </p>
                                    <p className="text-sm text-gray-500">{t('polygons.uploadGeoJSON')}</p>
                                </div>
                            </Link>

                            <Link
                                to="/analysis"
                                className="flex items-center gap-4 p-4 rounded-xl border border-line dark:border-gray-700 hover:bg-cream dark:hover:bg-gray-800 transition-colors"
                            >
                                <div className="w-11 h-11 rounded-xl bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 flex items-center justify-center">
                                    <BarChart3 className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">
                                        {t('analysis.newAnalysis')}
                                    </p>
                                    <p className="text-sm text-gray-500">Sentinel-2 + SAR</p>
                                </div>
                            </Link>

                            <Link
                                to="/reports"
                                className="flex items-center gap-4 p-4 rounded-xl border border-line dark:border-gray-700 hover:bg-cream dark:hover:bg-gray-800 transition-colors"
                            >
                                <div className="w-11 h-11 rounded-xl bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 flex items-center justify-center">
                                    <FileText className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">
                                        {t('reports.generate')}
                                    </p>
                                    <p className="text-sm text-gray-500">{t('dashboard.pdfWithTraceability')}</p>
                                </div>
                            </Link>
                        </div>
                    </div>

                    {/* Two columns */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Compliance Summary */}
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                                {t('dashboard.complianceSummary')}
                            </h2>
                            {stats.compliance_summary.length === 0 ? (
                                <p className="text-gray-400 text-sm text-center py-4">
                                    {t('dashboard.noAnalysisYet')}
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    {stats.compliance_summary.map((item) => (
                                        <div key={item.status} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                            <div className="flex items-center gap-3">
                                                {item.status === 'compliant' && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                                                {item.status === 'non_compliant' && <XCircle className="w-5 h-5 text-red-600" />}
                                                {(item.status === 'inconclusive' || item.status === 'pending') && <AlertTriangle className="w-5 h-5 text-yellow-500" />}
                                                <span className="text-gray-700 dark:text-gray-300 capitalize">
                                                    {complianceLabelMap[item.status] || item.status}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <span className={`font-semibold ${complianceColorMap[item.status] || 'text-gray-400'}`}>
                                                    {item.count}
                                                </span>
                                                <div className="w-24 sm:w-32 h-2 rounded-full bg-gray-200 dark:bg-gray-700">
                                                    <div
                                                        className={`h-full rounded-full ${
                                                            item.status === 'compliant'
                                                                ? 'bg-green-600'
                                                                : item.status === 'non_compliant'
                                                                    ? 'bg-red-600'
                                                                    : 'bg-yellow-500'
                                                        }`}
                                                        style={{
                                                            width: `${stats.analysis_completed > 0 ? (item.count / stats.analysis_completed) * 100 : 0}%`,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Recent Activity */}
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                                {t('dashboard.recentActivity')}
                            </h2>
                            {stats.recent_activity.length === 0 ? (
                                <div className="text-center py-4">
                                    <p className="text-gray-400 text-sm">{t('dashboard.noRecentActivity')}</p>
                                    <Link to="/polygons" className="text-primary-600 text-sm hover:underline mt-2 inline-block">
                                        {t('dashboard.uploadFirstPolygon')}
                                    </Link>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {stats.recent_activity.map((activity) => (
                                        <div
                                            key={activity.id}
                                            className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                                    <BarChart3 className="w-5 h-5" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900 dark:text-white">
                                                        {activity.name}
                                                    </p>
                                                    <p className="text-sm text-gray-500">
                                                        {activity.date
                                                            ? new Date(activity.date).toLocaleDateString(dateLocale, {
                                                                  day: '2-digit',
                                                                  month: 'short',
                                                                  year: 'numeric',
                                                              })
                                                            : ''}
                                                    </p>
                                                </div>
                                            </div>
                                            <span
                                                className={`text-sm font-medium ${
                                                    activity.status === 'completed'
                                                        ? 'badge-success'
                                                        : activity.status === 'processing'
                                                            ? 'badge-warning'
                                                            : activity.status === 'failed'
                                                                ? 'badge-danger'
                                                                : 'badge-info'
                                                }`}
                                            >
                                                {t(`analysis.status.${activity.status}`, { defaultValue: activity.status })}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// Stat Card Component
function StatCard({
    icon: Icon,
    label,
    value,
}: {
    icon: any;
    label: string;
    value: string;
    color: string;
    highlight?: boolean;
}) {
    return (
        <div className="card p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="w-11 h-11 rounded-xl bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
        </div>
    );
}
